import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
    Container,
    Typography,
    Grid,
    Button,
    Box,
    Card,
    CardContent,
    CardMedia,
    Dialog,
    DialogContent,
    IconButton,
    Chip
} from '@mui/material';
import {
    Engineering,
    Architecture,
    Construction,
    Business,
    KeyboardArrowDown,
    KeyboardArrowRight,
    KeyboardArrowLeft,
    SquareFoot,
    CalendarToday,
    Foundation,
    Build,
    AccountBalance,
    Layers,
    WaterDrop,
    VerifiedUser,
    HomeWork,
    LocationOn,
    Close,
    NavigateBefore,
    NavigateNext,
    FilterList,
    CorporateFare,
    Landscape,
    ElectricBolt,
    HomeRepairService,
    FormatPaint
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import Preloader from '../components/Preloader';
import { projectService } from '../services/api';
import '../styles/Home.scss';
import { API_URL } from '../services/api';
import { useSwipeable } from 'react-swipeable';

const Home = () => {
    const [loading, setLoading] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [featuredProjects, setFeaturedProjects] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 960);

    const backgroundImages = [
        '0f7228d1-ff7f-426e-b032-a2d1f2d26424.jpg',
        'f286bfee-f57e-48a8-90a8-7c3585dbfda5.jpg',
        '06669e05-4901-467b-b2e5-4452890f8951.jpg'
    ];

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 960);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 2000);
        const imageInterval = setInterval(() => {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === backgroundImages.length - 1 ? 0 : prevIndex + 1
            );
        }, 5000);

        // Kiemelt projektek lekérése
        const fetchFeaturedProjects = async () => {
            try {
                const response = await projectService.getFeaturedProjects();
                setFeaturedProjects(response.projects);
            } catch (error) {
                console.error('Hiba a kiemelt projektek betöltése során:', error);
            }
        };

        fetchFeaturedProjects();

        return () => {
            clearTimeout(timer);
            clearInterval(imageInterval);
        };
    }, []);

    const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [referencesRef, referencesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [expertiseRef, expertiseInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.1 });

    const mainServices = [
        {
            title: 'Elektromos munkák',
            description: 'Épületvillamossági kivitelezések és felújítás.',
            icon: <Engineering fontSize="large" />,
            link: '/szolgaltatasok#ipari'
        },
        {
            title: 'Toronyépítés',
            description: 'Távközlési tornyok és speciális építmények kivitelezése',
            icon: <Architecture fontSize="large" />,
            link: '/szolgaltatasok#torony'
        },
        {
            title: 'Közintézmények',
            description: 'Közintézmények és társasházak teljes körű kivitelezése',
            icon: <Business fontSize="large" />,
            link: '/szolgaltatasok#kereskedelmi'
        },
        {
            title: 'Speciális Építmények',
            description: 'Egyedi igényekre szabott építmények kivitelezése',
            icon: <Construction fontSize="large" />,
            link: '/szolgaltatasok#specialis'
        }
    ];

    const expertise = {
        technologies: [
            { icon: <Foundation />, text: 'Vasbeton szerkezetek' },
            { icon: <Build />, text: 'Acélszerkezet építés' },
            { icon: <AccountBalance />, text: 'Toronyépítési technológiák' },
            { icon: <Layers />, text: 'Ipari padlók kivitelezése' },
            { icon: <HomeWork />, text: 'Különböző falazati technológiák' },
            { icon: <WaterDrop />, text: 'Víz és hőszigetelés technológiák' }
        ],
        certifications: [
            { icon: <VerifiedUser />, text: 'MSZ EN 1090 acélszerkezet gyártás' },
            { icon: <Engineering />, text: 'Építőipari kivitelezői nyilvántartás' }
        ]
    };
    const foundingYear = 2005;
    const currentYear = new Date().getFullYear();
    const experienceYears = currentYear - foundingYear;
    const stats = [
        { number: `${experienceYears}+`, label: 'Év szakmai tapasztalat' },
        { number: '150+', label: 'Sikeres projekt' },
        { number: '50+', label: 'Szakértő munkatárs' },
        { number: '100%', label: 'Elégedett ügyfél' }
    ];

    const scrollToContent = () => {
        window.scrollTo({
            top: window.innerHeight,
            behavior: 'smooth'
        });
    };

    const handleProjectClick = (project) => {
        setSelectedProject(project);
    };

    const handleImageClick = (image, project) => {
        setSelectedImage(image);
        setSelectedProject(project);
    };

    const handleCloseImageViewer = () => {
        setSelectedImage(null);
    };

    const handlePrevImage = () => {
        if (selectedProject && selectedProject.images) {
            const currentIndex = selectedProject.images.indexOf(selectedImage);
            const newIndex = currentIndex > 0 ? currentIndex - 1 : selectedProject.images.length - 1;
            setSelectedImage(selectedProject.images[newIndex]);
        }
    };

    const handleNextImage = () => {
        if (selectedProject && selectedProject.images) {
            const currentIndex = selectedProject.images.indexOf(selectedImage);
            const newIndex = currentIndex < selectedProject.images.length - 1 ? currentIndex + 1 : 0;
            setSelectedImage(selectedProject.images[newIndex]);
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'pending': return 'Függőben';
            case 'in_progress': return 'Folyamatban';
            case 'completed': return 'Befejezett';
            case 'cancelled': return 'Törölve';
            default: return status;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'in_progress': return 'primary';
            case 'completed': return 'success';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            if (currentPage < Math.ceil(featuredProjects.length / (isMobile ? 1 : 3)) - 1) {
                setCurrentPage(prev => prev + 1);
            }
        },
        onSwipedRight: () => {
            if (currentPage > 0) {
                setCurrentPage(prev => prev - 1);
            }
        },
        preventDefaultTouchmoveEvent: true,
        trackMouse: false
    });

    // Projekt típusok definíciója
    const projectTypes = [
        { value: 'all', label: 'Összes típus', icon: <FilterList /> },
        { value: 'commercial', label: 'Kereskedelmi ingatlan', icon: <CorporateFare /> },
        { value: 'alpine', label: 'Alpintechnika', icon: <Landscape /> },
        { value: 'electrical', label: 'Épületvillamosság', icon: <ElectricBolt /> },
        { value: 'insulation', label: 'Szigeteléstechnika', icon: <Construction /> },
        { value: 'renovation', label: 'Felújítás', icon: <HomeRepairService /> },
        { value: 'facade', label: 'Homlokzati munkák', icon: <Business /> },
        { value: 'waterproofing', label: 'Vízszigetelés', icon: <WaterDrop /> },
        { value: 'painting', label: 'Festés-mázolás', icon: <FormatPaint /> }
    ];

    // Referencia szekció renderelése
    const renderReferences = () => {
        if (featuredProjects.length === 0) {
            return (
                <Typography variant="h6" align="center" color="textSecondary">
                    Jelenleg nincsenek kiemelt referenciák.
                </Typography>
            );
        }

        const isMobile = window.innerWidth <= 960;
        const itemsPerPage = isMobile ? 1 : 3;
        const totalPages = Math.ceil(featuredProjects.length / itemsPerPage);
        const startIndex = currentPage * itemsPerPage;
        const visibleProjects = featuredProjects.slice(startIndex, startIndex + itemsPerPage);

        return (
            <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                <Box {...swipeHandlers}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentPage}
                            initial={{ opacity: 0, x: 100 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                        >
                            <Grid 
                                container 
                                spacing={4} 
                                justifyContent="center"
                            >
                                {visibleProjects.map((project, index) => (
                                    <Grid 
                                        item 
                                        xs={12} 
                                        sm={isMobile ? 12 : visibleProjects.length === 1 ? 8 : 6} 
                                        md={isMobile ? 12 : visibleProjects.length === 1 ? 6 : 4} 
                                        key={project.id}
                                    >
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.2, duration: 0.5 }}
                                        >
                                            <Card 
                                                className="reference-card"
                                                onClick={() => handleProjectClick(project)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <div className="image-container">
                                                    <CardMedia
                                                        component="img"
                                                        image={project.images && project.images.length > 0 
                                                            ? `${API_URL}/uploads/projects/${project.images[0]}`
                                                            : '/images/placeholder.jpg'}
                                                        alt={project.title}
                                                        className="project-image"
                                                    />
                                                    <div className="image-overlay">
                                                        <Button variant="contained" className="view-details">
                                                            Részletek megtekintése
                                                        </Button>
                                                    </div>
                                                </div>
                                                <CardContent>
                                                    <Typography className="project-title">
                                                        {project.title}
                                                    </Typography>
                                                    <Typography className="project-description">
                                                        {project.description}
                                                    </Typography>
                                                    <div className="project-meta">
                                                        <div className="location">
                                                            <LocationOn />
                                                            {project.location || 'Magyarország'}
                                                        </div>
                                                        <div className="category">
                                                            {projectTypes.find(t => t.value === project.type)?.label || 'Építőipar'}
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    </Grid>
                                ))}
                            </Grid>
                        </motion.div>
                    </AnimatePresence>
                </Box>
                {totalPages > 1 && !isMobile && (
                    <Box 
                        sx={{ 
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2,
                            mt: 4
                        }}
                    >
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <IconButton
                                className="slider-button prev"
                                disabled={currentPage === 0}
                                onClick={() => setCurrentPage(prev => prev - 1)}
                                sx={{
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                        bgcolor: 'white',
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: 'rgba(255, 255, 255, 0.5)',
                                    }
                                }}
                            >
                                <KeyboardArrowLeft />
                            </IconButton>
                        </motion.div>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {[...Array(totalPages)].map((_, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: currentPage === index ? 'primary.main' : 'rgba(0, 0, 0, 0.2)',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onClick={() => setCurrentPage(index)}
                                />
                            ))}
                        </Box>
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <IconButton
                                className="slider-button next"
                                disabled={currentPage >= totalPages - 1}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                sx={{
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                        bgcolor: 'white',
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: 'rgba(255, 255, 255, 0.5)',
                                    }
                                }}
                            >
                                <KeyboardArrowRight />
                            </IconButton>
                        </motion.div>
                    </Box>
                )}
                {totalPages > 1 && isMobile && (
                    <Box 
                        sx={{ 
                            position: 'absolute',
                            bottom: 16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            gap: 1,
                            zIndex: 2
                        }}
                    >
                        {[...Array(totalPages)].map((_, index) => (
                            <Box
                                key={index}
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: currentPage === index ? 'primary.main' : 'rgba(0, 0, 0, 0.2)',
                                    transition: 'all 0.3s ease'
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <>
            <Helmet>
                <title>Toronyszerver KFT - Ipari és Kereskedelmi Építőipari Kivitelezés</title>
                <meta
                    name="description"
                    content={`Ipari létesítmények, távközlési tornyok és kereskedelmi ingatlanok professzionális kivitelezése. ${experienceYears}+ év szakmai tapasztalat, országos referenciák.`}
                />
            </Helmet>

            <AnimatePresence>
                {loading && <Preloader />}
            </AnimatePresence>

            <div className="home-page">
                {/* Hero Section */}
                <motion.div
                    className="hero-section"
                    ref={heroRef}
                    initial={{ opacity: 0 }}
                    animate={heroInView ? { opacity: 1 } : {}}
                    transition={{ duration: 1 }}
                >
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentImageIndex}
                            className="hero-background"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1 }}
                            style={{
                                backgroundImage: `url('/images/${backgroundImages[currentImageIndex]}')`
                            }}
                        >
                            <div className="overlay"></div>
                        </motion.div>
                    </AnimatePresence>

                    <Container>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <motion.div
                                    initial={{ x: -100, opacity: 0 }}
                                    animate={heroInView ? { x: 0, opacity: 1 } : {}}
                                    transition={{ delay: 0.2, duration: 0.8 }}
                                >
                                    <Typography variant="h1" className="hero-title">
                                        Ön megálmodja, Mi megépítjük
                                    </Typography>
                                    <Typography variant="h2" className="hero-subtitle">
                                        Épületek • Csarnokok • Tornyok
                                    </Typography>
                                    <Typography variant="body1" className="hero-description">
                                    {experienceYears}+ év szakmai tapasztalat, országos referenciák. Komplett kivitelezés az alapoktól a kulcsátadásig.
                                    </Typography>
                                    <Box className="hero-buttons">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            className="cta-button"
                                            href="/kapcsolat"
                                        >
                                            Ajánlatkérés
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="large"
                                            className="secondary-button"
                                            href="/projektek"
                                        >
                                            Referenciák
                                        </Button>
                                    </Box>
                                </motion.div>
                            </Grid>
                        </Grid>
                    </Container>

                    <motion.div
                        className="scroll-indicator"
                        onClick={scrollToContent}
                    >
                        <div className="chevron" />
                        <div className="chevron" />
                        <div className="chevron" />
                    </motion.div>
                </motion.div>

                {/* Main Services Section */}
                <motion.div
                    className="services-section"
                    ref={servicesRef}
                    initial={{ opacity: 0 }}
                    animate={servicesInView ? { opacity: 1 } : {}}
                    transition={{ duration: 1 }}
                >
                    <Container>
                        <Typography variant="h2" className="section-title" align="center" gutterBottom>
                            Fő Szolgáltatásaink
                        </Typography>
                        <Grid container spacing={4}>
                            {mainServices.map((service, index) => (
                                <Grid item xs={12} md={6} lg={3} key={index}>
                                    <motion.div
                                        initial={{ y: 50, opacity: 0 }}
                                        animate={servicesInView ? { y: 0, opacity: 1 } : {}}
                                        transition={{ delay: index * 0.2, duration: 0.5 }}
                                    >
                                        <Card 
                                            className="service-card"
                                            sx={{ 
                                                height: '100%',
                                                minHeight: '280px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'flex-start',
                                                p: 2,
                                                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                                                }
                                            }}
                                        >
                                            <CardContent sx={{ 
                                                flexGrow: 1, 
                                                textAlign: 'center',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                height: '100%'
                                            }}>
                                                <Box>
                                                    <Box 
                                                        className="icon-wrapper" 
                                                        sx={{ 
                                                            mb: 2,
                                                            color: 'primary.main',
                                                            '& > svg': {
                                                                fontSize: '3rem'
                                                            }
                                                        }}
                                                    >
                                                        {service.icon}
                                                    </Box>
                                                    <Typography 
                                                        variant="h5" 
                                                        className="service-title"
                                                        gutterBottom
                                                        sx={{ 
                                                            fontWeight: 'bold',
                                                            mb: 2
                                                        }}
                                                    >
                                                        {service.title}
                                                    </Typography>
                                                </Box>
                                                <Typography 
                                                    variant="body1" 
                                                    className="service-description"
                                                    color="text.secondary"
                                                >
                                                    {service.description}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                        <Box sx={{ mt: 6, textAlign: 'center' }}>
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    endIcon={<KeyboardArrowRight />}
                                    href="/szolgaltatasok"
                                    sx={{
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    Összes szolgáltatás
                                </Button>
                            </motion.div>
                        </Box>
                    </Container>
                </motion.div>

                {/* References Section */}
                <motion.section
                    className="references-section"
                    ref={referencesRef}
                    initial={{ opacity: 0, y: 50 }}
                    animate={referencesInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                >
                    <Container>
                        <Box sx={{ textAlign: 'center', mb: 6 }}>
                            <Typography variant="h2" className="section-title" gutterBottom>
                                Kiemelt Referenciáink
                            </Typography>
                            <Typography variant="subtitle1" className="section-subtitle">
                                Megvalósult projektjeink, amelyekre büszkék vagyunk
                            </Typography>
                        </Box>
                        {renderReferences()}
                        <Box mt={6} textAlign="center">
                            <Button
                                variant="outlined"
                                color="primary"
                                size="large"
                                endIcon={<KeyboardArrowRight />}
                                href="/projektek"
                            >
                                További referenciák
                            </Button>
                        </Box>
                    </Container>
                </motion.section>

                {/* Expertise Section */}
                <motion.div
                    className="expertise-section"
                    ref={expertiseRef}
                    initial={{ opacity: 0 }}
                    animate={expertiseInView ? { opacity: 1 } : {}}
                    transition={{ duration: 1 }}
                >
                    <Container>
                        <Box sx={{ textAlign: 'center', mb: 6 }}>
                            <Typography variant="h2" className="section-title" gutterBottom>
                                Szakértelem és Technológia
                            </Typography>
                            
                        </Box>
                        <Grid container spacing={4} justifyContent="center">
                            {[...expertise.technologies, ...expertise.certifications].map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <motion.div
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={expertiseInView ? { y: 0, opacity: 1 } : {}}
                                        transition={{ delay: index * 0.1, duration: 0.5 }}
                                    >
                                        <Card 
                                            sx={{ 
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                p: 2,
                                                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                        >
                                            <Box 
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    gap: 2
                                                }}
                                            >
                                                <Box 
                                                    sx={{ 
                                                        color: 'primary.main',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        '& > svg': {
                                                            fontSize: '2rem'
                                                        }
                                                    }}
                                                >
                                                    {item.icon}
                                                </Box>
                                                <Typography 
                                                    variant="body1"
                                                    sx={{ 
                                                        fontWeight: 500,
                                                        flex: 1
                                                    }}
                                                >
                                                    {item.text}
                                                </Typography>
                                            </Box>
                                        </Card>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </motion.div>

                {/* Stats Section */}
                <motion.div
                    className="stats-section"
                    ref={statsRef}
                    initial={{ opacity: 0 }}
                    animate={statsInView ? { opacity: 1 } : {}}
                    transition={{ duration: 1 }}
                >
                    <Container>
                        <Grid container spacing={4}>
                            {stats.map((stat, index) => (
                                <Grid item xs={6} md={3} key={index}>
                                    <motion.div
                                        initial={{ y: 30, opacity: 0 }}
                                        animate={statsInView ? { y: 0, opacity: 1 } : {}}
                                        transition={{ delay: index * 0.2, duration: 0.5 }}
                                        className="stat-card"
                                    >
                                        <Typography variant="h3" className="stat-number">
                                            {stat.number}
                                        </Typography>
                                        <Typography variant="body1" className="stat-label">
                                            {stat.label}
                                        </Typography>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </motion.div>

                {/* CTA Section */}
                <div className="cta-section">
                    <Container>
                        <motion.div
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.8 }}
                            className="cta-content"
                        >
                            <Typography variant="h3" align="center">
                                Kezdje meg projektjét velünk
                            </Typography>
                            <Typography variant="body1" align="center">
                                Vegye fel velünk a kapcsolatot és segítünk megvalósítani elképzeléseit!
                            </Typography>
                            <Box className="cta-button-wrapper">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    href="/kapcsolat"
                                >
                                    Kapcsolatfelvétel
                                </Button>
                            </Box>
                        </motion.div>
                    </Container>
                </div>
            </div>

            {/* Projekt részletek dialog */}
            <Dialog
                open={!!selectedProject}
                onClose={() => setSelectedProject(null)}
                maxWidth="lg"
                fullWidth
                className="project-dialog"
            >
                {selectedProject && (
                    <>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <DialogContent className="dialog-content">
                                <IconButton
                                    onClick={() => setSelectedProject(null)}
                                    className="close-button"
                                    sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: 8,
                                        zIndex: 1
                                    }}
                                >
                                    <Close />
                                </IconButton>

                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={7}>
                                        <div className="project-gallery">
                                            <div
                                                className="main-image"
                                                onClick={() => handleImageClick(selectedImage || selectedProject.images[0], selectedProject)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <img
                                                    src={selectedImage
                                                        ? `${API_URL}/uploads/projects/${selectedImage}`
                                                        : selectedProject.images && selectedProject.images.length > 0
                                                            ? `${API_URL}/uploads/projects/${selectedProject.images[0]}`
                                                            : '/images/placeholder.jpg'
                                                    }
                                                    alt={selectedProject.title}
                                                    style={{
                                                        width: '100%',
                                                        height: '400px',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                            </div>
                                            {selectedProject.images && selectedProject.images.length > 1 && (
                                                <Box 
                                                    sx={{ 
                                                        display: 'flex',
                                                        gap: 1,
                                                        mt: 2,
                                                        flexWrap: 'wrap'
                                                    }}
                                                >
                                                    {selectedProject.images.map((image, index) => (
                                                        <Box
                                                            key={index}
                                                            onClick={() => handleImageClick(image, selectedProject)}
                                                            sx={{
                                                                width: 80,
                                                                height: 80,
                                                                borderRadius: 1,
                                                                overflow: 'hidden',
                                                                cursor: 'pointer',
                                                                border: selectedImage === image ? '2px solid #2c5049' : 'none'
                                                            }}
                                                        >
                                                            <img
                                                                src={`${API_URL}/uploads/projects/${image}`}
                                                                alt={`${selectedProject.title} - ${index + 1}`}
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <div className="project-details">
                                            <Typography variant="h4" gutterBottom>
                                                {selectedProject.title}
                                            </Typography>

                                            <Chip
                                                label={getStatusLabel(selectedProject.status)}
                                                color={getStatusColor(selectedProject.status)}
                                                sx={{ mb: 2 }}
                                            />

                                            <Box sx={{ mb: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <LocationOn sx={{ mr: 1 }} />
                                                    <Typography>
                                                        <strong>Helyszín:</strong> {selectedProject.location || 'Magyarország'}
                                                    </Typography>
                                                </Box>
                                                {selectedProject.start_date && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <CalendarToday sx={{ mr: 1 }} />
                                                        <Typography>
                                                            <strong>Időtartam:</strong>{' '}
                                                            {new Date(selectedProject.start_date).toLocaleDateString('hu')}
                                                            {selectedProject.end_date && ` - ${new Date(selectedProject.end_date).toLocaleDateString('hu')}`}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {selectedProject.area && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <SquareFoot sx={{ mr: 1 }} />
                                                        <Typography>
                                                            <strong>Alapterület:</strong> {selectedProject.area} m²
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>

                                            {selectedProject.description && (
                                                <Box sx={{ mb: 3 }}>
                                                    <Typography variant="h6" gutterBottom>
                                                        Projekt leírása
                                                    </Typography>
                                                    <Typography>
                                                        {selectedProject.description}
                                                    </Typography>
                                                </Box>
                                            )}

                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                href={`/kapcsolat?project=${encodeURIComponent(selectedProject.title)}`}
                                                sx={{ mt: 2 }}
                                            >
                                                Kapcsolatfelvétel
                                            </Button>
                                        </div>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                        </motion.div>
                    </>
                )}
            </Dialog>

            {/* Nagyított kép dialog */}
            <Dialog
                open={!!selectedImage}
                onClose={handleCloseImageViewer}
                maxWidth="xl"
                fullWidth
                className="image-dialog"
                PaperProps={{
                    style: {
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        boxShadow: 'none',
                    },
                }}
            >
                <DialogContent sx={{ position: 'relative', p: 0, overflow: 'hidden', height: '90vh' }}>
                    <IconButton
                        onClick={handleCloseImageViewer}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                            },
                            zIndex: 2,
                        }}
                    >
                        <Close />
                    </IconButton>

                    {/* Navigációs gombok */}
                    {selectedProject && selectedProject.images && selectedProject.images.length > 1 && (
                        <>
                            <IconButton
                                onClick={handlePrevImage}
                                sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                    zIndex: 2,
                                }}
                            >
                                <NavigateBefore />
                            </IconButton>
                            <IconButton
                                onClick={handleNextImage}
                                sx={{
                                    position: 'absolute',
                                    right: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                    zIndex: 2,
                                }}
                            >
                                <NavigateNext />
                            </IconButton>
                        </>
                    )}

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                        }}
                    >
                        <Box
                            component="img"
                            src={`${API_URL}/uploads/projects/${selectedImage}`}
                            alt="Nagyított kép"
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                userSelect: 'none',
                                pointerEvents: 'none',
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </motion.div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Home; 